/**
 * createApiErrorHandler — 클라이언트 공통 API 에러 핸들러 팩토리
 *
 * configure()의 onError 콜백으로 주입해 useApiMutation 에러를 전역 처리한다.
 * toast, redirect 등 프로젝트별 로직은 옵션으로 주입한다.
 *
 * 서버 응답 형식: { detail?: string, message?: string, fieldErrors?: unknown[] }
 * fieldErrors가 있으면(zod 검증 에러) message를 우선 추출하고,
 * 없으면 detail을 우선 추출한다(그 다음 message).
 *
 * @example
 * // providers.tsx
 * import { createApiErrorHandler, configure } from "@sunkim4638/admin-modules/api-hooks";
 * import { toast } from "sonner";
 *
 * const handleApiErr = createApiErrorHandler({
 *   onMessage: (msg) => toast.error(msg),
 *   onUnauthorized: () => window.location.href = "/login",
 * });
 *
 * configure({ axios: ax, onError: handleApiErr });
 */

import axios from "axios";

export interface ApiErrorHandlerOptions {
    /**
     * 에러 메시지를 표시할 함수.
     * toast.error, alert 등 프로젝트에 맞게 주입한다.
     * 기본: console.error
     */
    onMessage?: (message: string) => void;
    /** 401 Unauthorized 처리 — 기본: 아무것도 안 함 */
    onUnauthorized?: () => void;
    /** 403 Forbidden 처리 — 기본: onMessage로 "권한이 없습니다." 표시 */
    onForbidden?: () => void;
    /**
     * 서버 응답에서 메시지를 추출하는 함수
     * 기본: data.detail → data.message 순으로 추출
     */
    extractMessage?: (data: any) => string | undefined;
    /** 개발 환경에서 console.error 출력 여부 — 기본: true */
    debug?: boolean;
}

export function createApiErrorHandler(options: ApiErrorHandlerOptions = {}) {
    const {
        onMessage,
        onUnauthorized,
        onForbidden,
        extractMessage = (data) =>
            data?.fieldErrors ? data?.message : data?.detail || data?.message,
        debug = true,
    } = options;

    const showMessage = onMessage ?? ((msg: string) => console.error("[api-error]", msg));

    return function handleApiErr(error: unknown): void {
        if (debug && process.env.NODE_ENV === "development") {
            console.error("[api-error]", error);
        }

        if (!axios.isAxiosError(error)) {
            showMessage(error instanceof Error ? error.message : "알 수 없는 오류가 발생했습니다.");
            return;
        }

        const status = error.response?.status;
        const message =
            extractMessage(error.response?.data) ??
            `API 오류 (${status ?? "unknown"})`;

        if (status === 401) {
            onUnauthorized?.();
            return;
        }

        if (status === 403) {
            if (onForbidden) {
                onForbidden();
            } else {
                showMessage("권한이 없습니다.");
            }
            return;
        }

        showMessage(message);
    };
}
